import {
  useCallback,
  useRef,
  useState,
  type PointerEventHandler,
  type RefObject,
} from 'react';

import {
  normalizePointerAngleDeltaDeg,
  pointerAngleDeg,
} from './product-groups-donut-rotation.helpers';

type ActiveDrag = {
  pointerId: number;
  lastAngleDeg: number;
};

export type ProductGroupsDonutRotation = {
  offsetDeg: number;
  isDragging: boolean;
  slotRef: RefObject<HTMLDivElement | null>;
  onPointerDown: PointerEventHandler<HTMLDivElement>;
  onPointerMove: PointerEventHandler<HTMLDivElement>;
  onPointerUp: PointerEventHandler<HTMLDivElement>;
  onPointerCancel: PointerEventHandler<HTMLDivElement>;
};

/**
 * Drag-to-rotate for the product-groups donut. Pointer deltas are applied to
 * {@link ProductGroupsDonutRotation.offsetDeg}, which callers pass into slice
 * geometry builders so leader lines stay aligned.
 */
export const useProductGroupsDonutRotation = (): ProductGroupsDonutRotation => {
  const slotRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<ActiveDrag | null>(null);
  const [offsetDeg, setOffsetDeg] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const getSlotCenter = useCallback(() => {
    const rect = slotRef.current?.getBoundingClientRect();
    if (!rect) return undefined;
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }, []);

  const onPointerDown = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      const center = getSlotCenter();
      if (!center) return;

      dragRef.current = {
        pointerId: event.pointerId,
        lastAngleDeg: pointerAngleDeg(
          event.clientX,
          event.clientY,
          center.x,
          center.y,
        ),
      };
      setIsDragging(true);
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    [getSlotCenter],
  );

  const onPointerMove = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      const center = getSlotCenter();
      if (!center) return;

      const angleDeg = pointerAngleDeg(
        event.clientX,
        event.clientY,
        center.x,
        center.y,
      );
      const deltaDeg = normalizePointerAngleDeltaDeg(
        angleDeg - drag.lastAngleDeg,
      );
      drag.lastAngleDeg = angleDeg;
      setOffsetDeg((previous) => previous + deltaDeg);
    },
    [getSlotCenter],
  );

  const endDrag = useCallback<PointerEventHandler<HTMLDivElement>>((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    dragRef.current = null;
    setIsDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  return {
    offsetDeg,
    isDragging,
    slotRef,
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  };
};
